import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  actualizarUsuario,
  crearUsuario,
  listarEmpresas,
  listarUsuarios,
} from "@/lib/api";
import type { CrearUsuarioPayload } from "@/lib/api";
import type { Rol, Usuario } from "@/types";
import { Header } from "@/components/Header";
import { Dialog } from "@/components/ui/Dialog";
import { useAuth } from "@/hooks/useAuth";
import { extractApiError } from "@/lib/utils";

const ROLES: { valor: Rol; etiqueta: string; nota: string }[] = [
  { valor: "analista", etiqueta: "Analista", nota: "Bandeja y gestión operativa" },
  { valor: "supervisor", etiqueta: "Supervisor", nota: "Aprueba, rechaza y notifica" },
  { valor: "administrador", etiqueta: "Administrador", nota: "Control de plataforma y usuarios" },
];

// Fallback mientras carga la lista dinámica (o si el endpoint falla).
const EMPRESAS_FALLBACK: { valor: string; etiqueta: string }[] = [
  { valor: "AFINIA", etiqueta: "AFINIA" },
  { valor: "AIR_E", etiqueta: "AIR-E" },
];

/** Opciones de empresa del selector, leídas del catálogo dinámico (/empresas). */
function useEmpresasOpciones(): { valor: string; etiqueta: string }[] {
  const { data } = useQuery({ queryKey: ["empresas"], queryFn: listarEmpresas });
  if (!data || data.length === 0) return EMPRESAS_FALLBACK;
  return data.map((e) => ({ valor: e.empresa, etiqueta: e.empresa }));
}

/** analista/supervisor quedan confinados a una empresa; admin/sistema son globales. */
function rolRequiereEmpresa(rol: Rol): boolean {
  return rol === "analista" || rol === "supervisor";
}

/** Administración de cuentas del backoffice — sólo administradores. */
export function UsuariosPage() {
  const qc = useQueryClient();
  const { sesion } = useAuth();
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [modalCrear, setModalCrear] = useState(false);
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [reseteando, setReseteando] = useState<Usuario | null>(null);

  const usuariosQ = useQuery({
    queryKey: ["usuarios", incluirInactivos],
    queryFn: () => listarUsuarios(incluirInactivos),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ["usuarios"] });

  const usuarios = usuariosQ.data ?? [];

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="page page--wide">
        <div className="crumbs">
          <Link to="/">Bandeja</Link>
          <span className="sep">/</span>
          <span>Usuarios</span>
        </div>

        <div className="page-head">
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Administración · Acceso
            </div>
            <h1>Usuarios del backoffice</h1>
            <p className="sub">
              Crea cuentas, asigna rol (analista, supervisor o administrador),
              resetea contraseñas y da de baja. Las contraseñas nuevas son
              provisionales: el titular debe cambiarlas al primer ingreso.
            </p>
          </div>
          <button className="btn btn--primary" onClick={() => setModalCrear(true)}>
            + Nuevo usuario
          </button>
        </div>

        <label
          className="row"
          style={{ gap: 8, marginBottom: 16, fontSize: 13, cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={incluirInactivos}
            onChange={(e) => setIncluirInactivos(e.target.checked)}
          />
          Mostrar cuentas inactivas
        </label>

        <div className="card">
          <div className="card__body" style={{ padding: 0 }}>
            {usuariosQ.isLoading ? (
              <p className="muted" style={{ padding: 16 }}>Cargando…</p>
            ) : usuarios.length === 0 ? (
              <p className="muted" style={{ padding: 16 }}>Sin usuarios.</p>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Nombre</th>
                    <th>Rol</th>
                    <th>Empresa</th>
                    <th>Estado</th>
                    <th>Último acceso</th>
                    <th style={{ textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr key={u.username}>
                      <td className="mono">{u.username}</td>
                      <td>{u.nombre || "—"}</td>
                      <td>
                        <span className={`badge badge--${badgeRol(u.rol)}`}>{u.rol}</span>
                      </td>
                      <td>
                        {u.empresa ? (
                          <span className="badge badge--ink">{u.empresa}</span>
                        ) : (
                          <span className="muted" style={{ fontSize: 12 }}>todas</span>
                        )}
                      </td>
                      <td>
                        {!u.activo ? (
                          <span className="badge badge--red">inactivo</span>
                        ) : u.bloqueado ? (
                          <span className="badge badge--amber">bloqueado</span>
                        ) : u.debe_cambiar_password ? (
                          <span className="badge badge--amber">clave provisional</span>
                        ) : (
                          <span className="badge badge--green">activo</span>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {u.ultimo_acceso
                          ? new Date(u.ultimo_acceso).toLocaleString("es-CO")
                          : "nunca"}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setEditando(u)}
                        >
                          Editar
                        </button>{" "}
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setReseteando(u)}
                        >
                          Resetear clave
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {modalCrear && (
        <ModalCrear onCerrar={() => setModalCrear(false)} onOk={invalidar} />
      )}
      {editando && (
        <ModalEditar
          usuario={editando}
          esYoMismo={editando.username === sesion?.sub}
          onCerrar={() => setEditando(null)}
          onOk={invalidar}
        />
      )}
      {reseteando && (
        <ModalResetear
          usuario={reseteando}
          onCerrar={() => setReseteando(null)}
          onOk={invalidar}
        />
      )}
    </div>
  );
}

function badgeRol(rol: Rol): string {
  return rol === "administrador" ? "ink" : rol === "supervisor" ? "amber" : "green";
}

// ─── Modal: crear ──────────────────────────────────────────────────── //

function ModalCrear({ onCerrar, onOk }: { onCerrar: () => void; onOk: () => void }) {
  const [form, setForm] = useState<CrearUsuarioPayload>({
    username: "",
    nombre: "",
    email: "",
    rol: "analista",
    empresa: "AFINIA",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      crearUsuario({
        ...form,
        email: form.email || null,
        // Los roles globales (admin/sistema) no llevan empresa.
        empresa: rolRequiereEmpresa(form.rol) ? form.empresa : null,
      }),
    onSuccess: () => {
      onOk();
      onCerrar();
    },
    onError: (e) => setError(extractApiError(e, "No se pudo crear el usuario.")),
  });

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mut.mutate();
  }

  const faltaEmpresa = rolRequiereEmpresa(form.rol) && !form.empresa;
  const listo =
    form.username.trim().length >= 3 &&
    form.password.length >= 10 &&
    !faltaEmpresa &&
    !mut.isPending;

  return (
    <Dialog
      abierto
      titulo="Nuevo usuario"
      onCerrar={onCerrar}
      acciones={
        <>
          <button className="btn btn--ghost" onClick={onCerrar}>Cancelar</button>
          <button className="btn btn--primary" onClick={enviar} disabled={!listo}>
            {mut.isPending ? "Creando…" : "Crear"}
          </button>
        </>
      }
    >
      <form onSubmit={enviar} className="stack stack-3">
        <CampoUsuario form={form} setForm={setForm} />
        <div className="field">
          <label>Contraseña provisional</label>
          <input
            className="input"
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="off"
            placeholder="mínimo 10 caracteres"
          />
          <span className="hint">
            El usuario deberá cambiarla en su primer ingreso.
          </span>
        </div>
        {error && (
          <div className="banner banner--red">
            <span className="banner__icon">⚠</span>
            <div>{error}</div>
          </div>
        )}
      </form>
    </Dialog>
  );
}

// ─── Modal: editar ─────────────────────────────────────────────────── //

function ModalEditar({
  usuario,
  esYoMismo,
  onCerrar,
  onOk,
}: {
  usuario: Usuario;
  esYoMismo: boolean;
  onCerrar: () => void;
  onOk: () => void;
}) {
  const empresas = useEmpresasOpciones();
  const [nombre, setNombre] = useState(usuario.nombre);
  const [email, setEmail] = useState(usuario.email ?? "");
  const [rol, setRol] = useState<Rol>(usuario.rol);
  const [empresa, setEmpresa] = useState(usuario.empresa ?? "AFINIA");
  const [activo, setActivo] = useState(usuario.activo);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      actualizarUsuario(usuario.username, {
        nombre,
        email: email || null,
        rol,
        // Solo se envía empresa para roles scoped; el backend la anula
        // para los globales cuando cambia el rol.
        ...(rolRequiereEmpresa(rol) ? { empresa } : {}),
        activo,
      }),
    onSuccess: () => {
      onOk();
      onCerrar();
    },
    onError: (e) => setError(extractApiError(e, "No se pudo actualizar.")),
  });

  return (
    <Dialog
      abierto
      titulo={`Editar ${usuario.username}`}
      onCerrar={onCerrar}
      acciones={
        <>
          <button className="btn btn--ghost" onClick={onCerrar}>Cancelar</button>
          <button
            className="btn btn--primary"
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <div className="stack stack-3">
        <div className="field">
          <label>Nombre</label>
          <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </div>
        <div className="field">
          <label>Correo</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label>Rol</label>
          <select
            className="select"
            value={rol}
            onChange={(e) => setRol(e.target.value as Rol)}
            disabled={esYoMismo}
          >
            {ROLES.map((r) => (
              <option key={r.valor} value={r.valor}>
                {r.etiqueta} — {r.nota}
              </option>
            ))}
          </select>
          {esYoMismo && (
            <span className="hint">No puedes cambiar tu propio rol.</span>
          )}
        </div>
        {rolRequiereEmpresa(rol) && (
          <div className="field">
            <label>Empresa</label>
            <select
              className="select"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
            >
              {empresas.map((em) => (
                <option key={em.valor} value={em.valor}>
                  {em.etiqueta}
                </option>
              ))}
            </select>
            <span className="hint">Tenant al que queda confinado el usuario.</span>
          </div>
        )}
        <label className="row" style={{ gap: 8, cursor: esYoMismo ? "not-allowed" : "pointer" }}>
          <input
            type="checkbox"
            checked={activo}
            disabled={esYoMismo}
            onChange={(e) => setActivo(e.target.checked)}
          />
          Cuenta activa
          {esYoMismo && <span className="hint">No puedes desactivarte a ti mismo.</span>}
        </label>
        {error && (
          <div className="banner banner--red">
            <span className="banner__icon">⚠</span>
            <div>{error}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ─── Modal: resetear contraseña ────────────────────────────────────── //

function ModalResetear({
  usuario,
  onCerrar,
  onOk,
}: {
  usuario: Usuario;
  onCerrar: () => void;
  onOk: () => void;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => actualizarUsuario(usuario.username, { password }),
    onSuccess: () => {
      onOk();
      onCerrar();
    },
    onError: (e) => setError(extractApiError(e, "No se pudo resetear.")),
  });

  return (
    <Dialog
      abierto
      titulo={`Resetear contraseña de ${usuario.username}`}
      onCerrar={onCerrar}
      acciones={
        <>
          <button className="btn btn--ghost" onClick={onCerrar}>Cancelar</button>
          <button
            className="btn btn--primary"
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
            disabled={password.length < 10 || mut.isPending}
          >
            {mut.isPending ? "Aplicando…" : "Resetear"}
          </button>
        </>
      }
    >
      <div className="stack stack-3">
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Se asigna una contraseña provisional y se levanta cualquier bloqueo.
          El usuario deberá cambiarla al ingresar.
        </p>
        <div className="field">
          <label>Nueva contraseña provisional</label>
          <input
            className="input"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            placeholder="mínimo 10 caracteres"
          />
        </div>
        {error && (
          <div className="banner banner--red">
            <span className="banner__icon">⚠</span>
            <div>{error}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ─── Campos compartidos ────────────────────────────────────────────── //

function CampoUsuario({
  form,
  setForm,
}: {
  form: CrearUsuarioPayload;
  setForm: (f: CrearUsuarioPayload) => void;
}) {
  const empresas = useEmpresasOpciones();
  return (
    <>
      <div className="field">
        <label>Usuario</label>
        <input
          className="input"
          value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
          placeholder="ana.gomez"
          autoComplete="off"
        />
      </div>
      <div className="field">
        <label>Nombre</label>
        <input
          className="input"
          value={form.nombre}
          onChange={(e) => setForm({ ...form, nombre: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Correo (opcional)</label>
        <input
          className="input"
          value={form.email ?? ""}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Rol</label>
        <select
          className="select"
          value={form.rol}
          onChange={(e) => setForm({ ...form, rol: e.target.value as Rol })}
        >
          {ROLES.map((r) => (
            <option key={r.valor} value={r.valor}>
              {r.etiqueta} — {r.nota}
            </option>
          ))}
        </select>
      </div>
      {rolRequiereEmpresa(form.rol) ? (
        <div className="field">
          <label>Empresa</label>
          <select
            className="select"
            value={form.empresa ?? ""}
            onChange={(e) => setForm({ ...form, empresa: e.target.value })}
          >
            {empresas.map((em) => (
              <option key={em.valor} value={em.valor}>
                {em.etiqueta}
              </option>
            ))}
          </select>
          <span className="hint">
            El usuario solo verá y gestionará expedientes de esta empresa.
          </span>
        </div>
      ) : (
        <span className="hint">
          Los roles administrador y sistema operan todas las empresas.
        </span>
      )}
    </>
  );
}
